import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReviewForm from '../ReviewForm';
import { useQuery } from 'react-query';
import { fetcher } from '../../../utils/api';
import { ApiRoutes } from '../../../constants/routes';
import { useAuthProvider } from '../../../utils/auth';
import { useMutation, useQueryClient } from 'react-query';
import { mutation } from '../../../utils/api';

const ReviewDialog = ({ open, onClose, contentType, contentId, existingReview = null }) => {
  const { user } = useAuthProvider();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  // Use the passed existingReview prop, or fetch the latest one if not provided
  // (for backward compatibility when opened from ReviewCard edit menu)
  const { data: fetchedUserReview } = useQuery({
    queryKey: ['user-review', contentType, contentId, user?.uuid],
    queryFn: () => {
      const userId = user?.uuid || user?.id;
      return fetcher(`${ApiRoutes.MusicReviews}?content_type=${contentType}&object_id=${contentId}&user_id=${userId}`);
    },
    enabled: !!user && !!contentType && !!contentId && open && !existingReview,
    select: (data) => data && data.length > 0 ? data[0] : null,
  });
  
  // Use existingReview prop if provided, otherwise use fetched one
  const userReview = existingReview || fetchedUserReview;

  // Delete review mutation
  const deleteReviewMutation = useMutation({
    mutationFn: () => mutation(ApiRoutes.MusicReviewDetail(userReview.id), {}, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries(['music-reviews', contentType, contentId]);
      queryClient.invalidateQueries(['user-review', contentType, contentId, user?.uuid]);
      queryClient.invalidateQueries(['review-overview', contentType, contentId]);
      onClose();
    },
  });

  const handleDelete = async () => {
    if (!userReview) return;
    if (window.confirm('Are you sure you want to delete your review? This action cannot be undone.')) {
      setIsDeleting(true);
      deleteReviewMutation.mutate();
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries(['music-reviews', contentType, contentId]);
    queryClient.invalidateQueries(['user-review', contentType, contentId, user?.uuid]);
    queryClient.invalidateQueries(['review-overview', contentType, contentId]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {userReview ? 'Edit Your Review' : 'Write a Review'}
        <IconButton
          onClick={onClose}
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ReviewForm
          contentType={contentType}
          contentId={contentId}
          existingReview={userReview}
          onSuccess={handleSuccess}
          onCancel={onClose}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {userReview && (
          <Button
            onClick={handleDelete}
            disabled={deleteReviewMutation.isLoading || isDeleting}
            sx={{
              color: 'error.main',
              mr: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              },
            }}
          >
            {deleteReviewMutation.isLoading || isDeleting ? 'Deleting...' : 'Delete Review'}
          </Button>
        )}
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewDialog;

